import React, { useState, useEffect, useRef } from "react";
import { Box, Stack, Typography, CircularProgress, Button, Tooltip } from "@mui/material";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import axios from "axios";
import { openMarkingPreviewWindow } from "../helper/markingPreviewWindow";

/**
 * The full-plate camera capture a patch group's measurements were read from.
 *
 * The stored object is a ~60 MP camera RAW, so the backend serves a downscaled
 * JPEG and caches it beside the raw; the first view of a given capture can still
 * take a couple of seconds. Clicking opens the image full size.
 *
 * A capture that will not load must never take its surroundings with it: an
 * unmeasured pass and a failed fetch both degrade to a quiet line of text.
 *
 * Props:
 *   explorationId, plateId, patchGroupId: string  address of the capture
 *   passKind: "COLOR" | "CLUSTER"                 which marking pass to show
 *   sourceImage: {id, file_name, ...} | null      from the patches / marking
 *                                                 endpoint; null means the pass
 *                                                 has not been measured
 *   caption: string                               optional line above the image
 *   emptyText: string                             optional "no capture" wording
 */
function CaptureImage({
  explorationId,
  plateId,
  patchGroupId,
  passKind,
  sourceImage,
  caption,
  emptyText = "No capture recorded for this pass yet.",
}) {
  const [imageUrl, setImageUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isDownloadingRaw, setIsDownloadingRaw] = useState(false);
  // Held so "open full size" reuses the bytes we already have instead of
  // re-requesting (and re-decoding) the capture.
  const blobRef = useRef(null);

  const sourceImageId = sourceImage?.id;

  useEffect(() => {
    let cancelled = false;
    let objectUrl = "";
    blobRef.current = null;
    setImageUrl("");
    setError("");

    if (!explorationId || !plateId || !patchGroupId || !sourceImageId) {
      return undefined;
    }

    setLoading(true);
    axios
      .get(
        `api/exploration/${explorationId}/plates/${plateId}/patch-group/${patchGroupId}/source-image/`,
        { params: { pass_kind: passKind }, responseType: "blob" }
      )
      .then((response) => {
        if (cancelled) return;
        blobRef.current = response.data;
        objectUrl = URL.createObjectURL(response.data);
        setImageUrl(objectUrl);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Capture could not be loaded.");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [explorationId, plateId, patchGroupId, passKind, sourceImageId]);

  if (!sourceImage) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyText}
      </Typography>
    );
  }

  const fileName = sourceImage.file_name || "capture";

  const handleDownloadRaw = async () => {
    if (isDownloadingRaw) return;
    setIsDownloadingRaw(true);
    try {
      const response = await axios.get(
        `api/exploration/${explorationId}/plates/${plateId}/patch-group/${patchGroupId}/source-image/`,
        { params: { pass_kind: passKind, raw: 1 }, responseType: "blob" }
      );
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError("RAW download failed.");
    } finally {
      setIsDownloadingRaw(false);
    }
  };

  return (
    // minWidth 0: a grid/flex item otherwise refuses to shrink below the
    // nowrap file name in the footer, which pushes sibling captures off-card.
    <Box sx={{ minWidth: 0 }}>
      {caption ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
          {caption}
        </Typography>
      ) : null}

      {loading ? (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2">Rendering capture...</Typography>
        </Stack>
      ) : null}

      {error ? (
        <Typography variant="body2" color="text.secondary">
          {error}
        </Typography>
      ) : null}

      {imageUrl ? (
        <Box
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Tooltip title="Open full size" arrow>
            <Box
              component="img"
              src={imageUrl}
              alt={`Capture ${fileName}`}
              onClick={() => {
                if (blobRef.current) {
                  openMarkingPreviewWindow(blobRef.current, `Capture - ${fileName}`);
                }
              }}
              sx={{
                display: "block",
                width: "100%",
                maxWidth: "100%",
                objectFit: "contain",
                cursor: "zoom-in",
                transition: "opacity 0.15s ease",
                "&:hover": { opacity: 0.92 },
              }}
            />
          </Tooltip>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            sx={{ px: 1.25, py: 0.75, borderTop: "1px solid", borderColor: "divider" }}
          >
            <Typography
              variant="caption"
              title={fileName}
              sx={{
                minWidth: 0,
                fontFamily: "monospace",
                color: "text.secondary",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {fileName}
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
              <Button
                size="small"
                variant="text"
                startIcon={<OpenInFullIcon sx={{ fontSize: 14 }} />}
                onClick={() => {
                  if (blobRef.current) {
                    openMarkingPreviewWindow(blobRef.current, `Capture - ${fileName}`);
                  }
                }}
                sx={{ textTransform: "none", fontSize: 12, minWidth: 0 }}
              >
                Full size
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={
                  isDownloadingRaw ? (
                    <CircularProgress size={12} />
                  ) : (
                    <FileDownloadOutlinedIcon sx={{ fontSize: 15 }} />
                  )
                }
                onClick={handleDownloadRaw}
                disabled={isDownloadingRaw}
                sx={{ textTransform: "none", fontSize: 12, borderRadius: 1.5 }}
              >
                RAW
              </Button>
            </Stack>
          </Stack>
        </Box>
      ) : null}
    </Box>
  );
}

export { CaptureImage };
export default CaptureImage;
